// src/admin/components/SupplierViewDrawer.tsx
import React, { useState } from 'react';
import {
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Stack,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { Supplier } from '../purchase-orders/PurchaseOrder';

interface SupplierViewDrawerProps {
  open: boolean;
  onClose: () => void;
  supplier: Supplier;
  onEdit: () => void;
  onDelete: () => void;
}

// ── Theme-aware chip style helper ─────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.12)',  border: 'rgba(255,167,38,0.4)',   text: '#b76e00', iconColor: '#e68900' },
    info:      { bg: 'rgba(41,182,246,0.12)',   border: 'rgba(41,182,246,0.4)',   text: '#0277bd', iconColor: '#039be5' },
    primary:   { bg: 'rgba(33,150,243,0.12)',   border: 'rgba(33,150,243,0.4)',   text: '#1565c0', iconColor: '#1976d2' },
    success:   { bg: 'rgba(102,187,106,0.15)',  border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error:     { bg: 'rgba(239,83,80,0.12)',    border: 'rgba(239,83,80,0.4)',    text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)',   border: 'rgba(171,71,188,0.35)',  text: '#6a1b9a', iconColor: '#8e24aa' },
    default:   { bg: 'rgba(158,158,158,0.12)',  border: 'rgba(158,158,158,0.4)',  text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.22)',  border: 'rgba(255,167,38,0.55)',  text: '#ffcc80', iconColor: '#ffa726' },
    info:      { bg: 'rgba(41,182,246,0.20)',   border: 'rgba(41,182,246,0.50)',  text: '#81d4fa', iconColor: '#29b6f6' },
    primary:   { bg: 'rgba(33,150,243,0.22)',   border: 'rgba(33,150,243,0.50)',  text: '#90caf9', iconColor: '#42a5f5' },
    success:   { bg: 'rgba(102,187,106,0.22)',  border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error:     { bg: 'rgba(239,83,80,0.22)',    border: 'rgba(239,83,80,0.50)',   text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)',   border: 'rgba(171,71,188,0.50)',  text: '#ce93d8', iconColor: '#ab47bc' },
    default:   { bg: 'rgba(158,158,158,0.22)',  border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

// ── InfoRow ───────────────────────────────────────────────────────────────────
interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  divider?: boolean;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value, divider = true }) => (
  <>
    <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ py: 1 }}>
      <Box sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}
        >
          {label}
        </Typography>
        <Box sx={{ mt: 0.25 }}>{value}</Box>
      </Box>
    </Stack>
    {divider && <Divider />}
  </>
);

const SupplierViewDrawer: React.FC<SupplierViewDrawerProps> = ({
  open,
  onClose,
  supplier,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // ── Colors sourced entirely from the MUI theme (set via themeConfig.ts) ──
  // drawerBg → background.default  (the page/shell background — darker in dark mode)
  // cardBg   → background.paper    (surface/card background — lighter in dark mode)
  //
  // To change these colours, edit themeConfig.ts:
  //   THEME_COLORS.background.default / DARK_THEME_COLORS.background.default
  //   THEME_COLORS.background.paper   / DARK_THEME_COLORS.background.paper
  // ─────────────────────────────────────────────────────────────────────────
  const drawerBg = theme.palette.background.default;
  const cardBg   = theme.palette.background.paper;

  // ── Delete confirmation dialog state ──────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  const handleDelete = () => setDeleteDialogOpen(true);

  const handleDeleteConfirm = async () => {
    try {
      setDeleteDialogLoading(true);
      await onDelete();
      setDeleteDialogOpen(false);
    } finally {
      setDeleteDialogLoading(false);
    }
  };

  const handleDeleteCancel = () => setDeleteDialogOpen(false);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 520 },
            bgcolor: drawerBg,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* ── Drawer Header ── */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              flexShrink: 0,
              bgcolor: drawerBg,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <BusinessIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    Supplier Details
                  </Typography>
                  <Chip
                    label={supplier.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    variant="outlined"
                    sx={{
                      mt: 0.5,
                      ...getSoftChipSx(supplier.isActive ? 'success' : 'default', isDark),
                    }}
                  />
                </Box>
              </Stack>
              <IconButton onClick={onClose} edge="end">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* ── Scrollable body ── */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 3,
              py: 3,
              minHeight: 0,
              bgcolor: drawerBg,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(128,128,128,0.3)', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(128,128,128,0.5)' },
            }}
          >
            <Stack spacing={3}>

              {/* Basic Information */}
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
                >
                  Basic Information
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ mt: 1, px: 2, py: 0.5, borderRadius: 1.5, bgcolor: cardBg }}
                >
                  <InfoRow
                    icon={<BusinessIcon fontSize="small" />}
                    label="Supplier Name"
                    value={
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {supplier.name}
                      </Typography>
                    }
                  />
                  <InfoRow
                    icon={<EmailIcon fontSize="small" />}
                    label="Email Address"
                    value={
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {supplier.email}
                      </Typography>
                    }
                  />
                  <InfoRow
                    icon={<PhoneIcon fontSize="small" />}
                    label="Phone Number"
                    value={<Typography variant="body2">{supplier.phone}</Typography>}
                    divider={false}
                  />
                </Paper>
              </Box>

              {/* Additional Details */}
              {(supplier.contactPerson || supplier.taxId || supplier.paymentTerms) && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
                  >
                    Additional Details
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ mt: 1, px: 2, py: 0.5, borderRadius: 1.5, bgcolor: cardBg }}
                  >
                    {supplier.contactPerson && (
                      <InfoRow
                        icon={<PersonIcon fontSize="small" />}
                        label="Contact Person"
                        value={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {supplier.contactPerson}
                          </Typography>
                        }
                        divider={!!(supplier.taxId || supplier.paymentTerms)}
                      />
                    )}
                    {supplier.taxId && (
                      <InfoRow
                        icon={<ReceiptIcon fontSize="small" />}
                        label="Tax ID"
                        value={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {supplier.taxId}
                          </Typography>
                        }
                        divider={!!supplier.paymentTerms}
                      />
                    )}
                    {supplier.paymentTerms && (
                      <InfoRow
                        icon={<PaymentIcon fontSize="small" />}
                        label="Payment Terms"
                        value={
                          <Chip
                            label={supplier.paymentTerms}
                            size="small"
                            variant="outlined"
                            sx={getSoftChipSx('info', isDark)}
                          />
                        }
                        divider={false}
                      />
                    )}
                  </Paper>
                </Box>
              )}

              {/* Address */}
              {supplier.address && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
                  >
                    Address
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ mt: 1, px: 2, py: 0.5, borderRadius: 1.5, bgcolor: cardBg }}
                  >
                    <InfoRow
                      icon={<LocationOnIcon fontSize="small" />}
                      label="Address"
                      value={
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {supplier.address}
                        </Typography>
                      }
                      divider={false}
                    />
                  </Paper>
                </Box>
              )}

              {/* Record Information */}
              {(supplier.createdAt || supplier.updatedAt) && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
                  >
                    Record Information
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ mt: 1, px: 2, py: 1.5, borderRadius: 1.5, bgcolor: cardBg }}
                  >
                    <Grid container spacing={2}>
                      {supplier.createdAt && (
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}
                          >
                            Created On
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {supplier.createdAt.toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {supplier.createdAt.toLocaleTimeString()}
                          </Typography>
                        </Grid>
                      )}
                      {supplier.updatedAt && (
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}
                          >
                            Last Updated
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {supplier.updatedAt.toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {supplier.updatedAt.toLocaleTimeString()}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Box>
              )}

            </Stack>
          </Box>

          {/* ── Drawer Footer ── */}
          <Divider />
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              flexShrink: 0,
              bgcolor: drawerBg,
            }}
          >
            {/* <Button
              onClick={handleDelete}
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button> */}
            <Box sx={{ flex: 1 }} />
            {/* <Button
              onClick={onEdit}
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
            >
              Edit
            </Button> */}
            <Button onClick={onClose} variant="contained" size="small">
              Close
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Supplier</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>"{supplier.name}"</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SupplierViewDrawer;